import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articlesContent } from '@/mocks/article-content';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? articlesContent[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Artykuł nie został znaleziony</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
            Powrót na stronę główną
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/artykul/${article.slug}`,
    },
    headline: article.title,
    description: article.description,
    image: article.heroImage,
    author: { '@type': 'Organization', name: 'Kakobuy Spreadsheet Polska' },
    publisher: { '@type': 'Organization', name: 'Kakobuy Spreadsheet Polska', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png' } },
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: 'pl-PL',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: `${import.meta.env.VITE_SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: article.title, item: `${import.meta.env.VITE_SITE_URL}/artykul/${article.slug}` },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <Navbar />

      <article className="pt-20 pb-16">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8 pb-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">Strona główna</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700">{article.title.length > 60 ? article.title.substring(0, 60) + '...' : article.title}</span>
          </nav>
        </div>

        {/* Article Header */}
        <header className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary-500 bg-primary-50 px-3 py-1 rounded-full">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">
              {new Date(article.date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
            {article.description}
          </p>
        </header>

        {/* Featured Image */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 mb-12">
          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden">
            <img src={article.heroImage} alt={article.title} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="space-y-8">
            {article.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>

                {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}

                {section.listItems && section.listItems.length > 0 && (
                  <ul className="space-y-3 mb-4">
                    {section.listItems.map((item, lIdx) => (
                      <li key={lIdx} className="flex items-start gap-3 text-sm text-foreground-700">
                        <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.table && section.table.headers && section.table.rows && (
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-background-100">
                          {section.table.headers.map((h, hIdx) => (
                            <th key={hIdx} className={`text-left px-4 py-3 font-semibold ${hIdx === 0 ? 'text-foreground-800' : 'text-primary-600'}`}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200/60">
                        {section.table.rows.map((row, rIdx) => (
                          <tr key={rIdx} className="hover:bg-background-100/50">
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className={`px-4 py-2.5 ${cIdx === 0 ? 'text-foreground-700' : 'text-primary-600 font-medium'}`}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {section.cta && (
                  <div className="bg-accent-50 border border-accent-200 rounded-lg p-5 mt-6">
                    <p className="text-sm font-semibold text-accent-800 mb-3">Gotowy, aby odkryć najlepsze produkty?</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={section.cta.link}
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {section.cta.text}
                        <i className="ri-arrow-right-up-line"></i>
                      </a>
                      <Link
                        to="/"
                        className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-foreground-700 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap border border-background-200"
                      >
                        <i className="ri-arrow-left-line"></i>
                        Powrót na stronę główną
                      </Link>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 pt-8 border-t border-background-200/60">
            <div className="bg-background-100 rounded-lg p-6 text-center">
              <p className="text-base font-heading font-semibold text-foreground-950 mb-2">
                Chcesz odkryć więcej produktów?
              </p>
              <p className="text-sm text-foreground-600 mb-5">
                Przeglądaj aktualny Kakobuy Spreadsheet i znajdź najlepsze okazje.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kakobuy Spreadsheet
                </a>
                <Link
                  to="/"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
                >
                  ← Powrót na stronę główną
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}